/**
 * A series at a glance — no axes, no labels, no interaction.
 *
 * The design puts one of these in every KPI box on the app dashboard. It says
 * one thing: which way this figure has been going inside the period on screen.
 * Anything more precise is a click away in the chart dialog, and adding a scale
 * to a 36×16 drawing would only make it look like it could be read off.
 *
 * A flat series and an empty one are both drawn as a flat line rather than as
 * nothing, because a box with no line reads as a broken box; the peak is what
 * the height is scaled against, so a single spike does not clip.
 */
export function Sparkline({
  points,
  color,
  label,
}: {
  points: number[]
  color: string
  /** For the screen reader — the box's own label. */
  label: string
}) {
  if (points.length === 0) return null
  const W = 36
  const H = 16
  const peak = Math.max(1, ...points)
  const d = points
    .map((v, i) => {
      const x = points.length === 1 ? W / 2 : 1 + (i / (points.length - 1)) * (W - 2)
      const y = H - 1 - (v / peak) * (H - 3)
      return `${x.toFixed(1)},${y.toFixed(1)}`
    })
    .join(' ')

  return (
    <svg
      className="spark"
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      role="img"
      aria-label={`${label} over the period`}
    >
      <polyline points={d} fill="none" stroke={color} strokeWidth="1.6" strokeLinejoin="round" />
    </svg>
  )
}
